/** Sortable read-only table for API ``{columns, rows}`` payloads. */
import { useMemo, useState } from "react";

import type { Table } from "../api/types";
import { dateTime } from "../lib/format";

type Formatter = (value: any, row: Record<string, any>) => React.ReactNode;

export function DataTable(props: {
  table: Table;
  columns?: string[];
  labels?: Record<string, string>;
  format?: Record<string, Formatter>;
  timeZone?: string;
  maxRows?: number;
  empty?: string;
  rowKey?: string;
}) {
  const columns = props.columns ?? props.table.columns;
  const [sort, setSort] = useState<{ column: string; asc: boolean } | null>(null);
  const rows = useMemo(() => {
    const data = [...props.table.rows];
    if (sort) {
      data.sort((a, b) => {
        const left = a[sort.column];
        const right = b[sort.column];
        if (left === right) return 0;
        if (left === null || left === undefined) return 1;
        if (right === null || right === undefined) return -1;
        return (left < right ? -1 : 1) * (sort.asc ? 1 : -1);
      });
    }
    return props.maxRows ? data.slice(0, props.maxRows) : data;
  }, [props.table.rows, props.maxRows, sort]);

  if (!props.table.rows.length) return <p className="muted small">{props.empty ?? "No rows."}</p>;

  const render = (column: string, row: Record<string, any>) => {
    const value = row[column];
    if (props.format?.[column]) return props.format[column](value, row);
    if (value === null || value === undefined) return "—";
    if (typeof value === "number") return Number.isInteger(value) ? value : value.toLocaleString("en-GB", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T.*Z$/.test(value) && props.timeZone) return dateTime(value, props.timeZone);
    return String(value);
  };

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column} aria-sort={sort?.column === column ? (sort.asc ? "ascending" : "descending") : "none"}>
                <button type="button" onClick={() => setSort((current) => ({ column, asc: current?.column === column ? !current.asc : true }))}>
                  {props.labels?.[column] ?? column}
                  {sort?.column === column ? (sort.asc ? " ▲" : " ▼") : ""}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={props.rowKey ? String(row[props.rowKey]) : index}>
              {columns.map((column) => (
                <td key={column} className={typeof row[column] === "number" ? "num" : undefined}>
                  {render(column, row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
